import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import {
  DayOfWeekEnum,
  ScheduleFrequencyEnum,
  ScheduleTypeEnum,
} from '@ulysses-inc/harami_api_client'
// 以下のESLintエラーは回避策がないようなので無視する https://github.com/import-js/eslint-plugin-import/issues/1479
import {
  addMonths,
  addWeeks,
  endOfDay,
  getWeekOfMonth,
  setMilliseconds,
  startOfMonth,
} from 'date-fns' // eslint-disable-line import/no-duplicates
import { ja } from 'date-fns/locale' // eslint-disable-line import/no-duplicates
import type React from 'react'
import FormikErrorMessage from 'src/components/formikErrorMessage/FormikErrorMessage'
import date from 'src/exShared/util/date'
import type { ScheduleForm, TBaseFormRowProps } from './EditSchedule'

/** shouldDisableDate 用の境界値を返す。この値未満の日付がカレンダーでグレーアウトされる */
export const getStartDateDisableBoundary = (schedule: ScheduleForm) => {
  const today = date.today()
  // OpenAPI上で月曜~日曜を1~7で設定しているので日曜のずれを補正する
  const todayWeek = today.getDay() !== 0 ? today.getDay() : DayOfWeekEnum.Sunday
  const yesterday = date.addDays(date.endOfDay(today), -1)

  // 頻度=毎日
  if (schedule.frequency === ScheduleFrequencyEnum.Daily) {
    return yesterday
  }

  // 頻度=週単位
  if (schedule.frequency === ScheduleFrequencyEnum.Weekly) {
    return yesterday
  }

  // 頻度=月単位
  if (schedule.frequency === ScheduleFrequencyEnum.Monthly) {
    const scheduleMonthlyEvery = schedule.frequencyMonthly?.every ?? 0
    const scheduleWeekOfMonth = schedule.frequencyMonthly?.weekOfMonth ?? 0
    const scheduleDayOfWeek = schedule.frequencyMonthly?.dayOfWeek ?? 0
    // タイプが期間の場合は最初の開始日は現在日から開始（現在日以前は非活性）
    if (schedule.type === ScheduleTypeEnum.Duration) return yesterday
    // 各サイクル設定が未設定の場合は現在日から開始（現在日以前は非活性）
    if (scheduleMonthlyEvery === 0) return yesterday
    if (scheduleWeekOfMonth === 0) return yesterday
    if (scheduleDayOfWeek === 0) return yesterday

    const todayWeekOfMonth = getWeekOfMonth(today)
    if (todayWeekOfMonth < scheduleWeekOfMonth) {
      const thisMonthFirstDay = date.startOfMonth(today)
      const todayMonthTargetWeekDay = addWeeks(
        thisMonthFirstDay,
        scheduleWeekOfMonth - 1,
      )
      const thisMonthTargetDayOfWeek =
        todayMonthTargetWeekDay.getDay() !== 0
          ? todayMonthTargetWeekDay.getDay()
          : DayOfWeekEnum.Sunday
      const diffDays = scheduleDayOfWeek - thisMonthTargetDayOfWeek
      const thisMonthTargetDay = date.addDays(todayMonthTargetWeekDay, diffDays)
      if (today.getMonth() === thisMonthTargetDay.getMonth()) {
        return thisMonthTargetDay
      }
    }

    if (todayWeekOfMonth === scheduleWeekOfMonth) {
      if (todayWeek <= scheduleDayOfWeek) {
        const diffDays = scheduleDayOfWeek - todayWeek
        const targetDate = date.addDays(today, diffDays)
        if (today.getMonth() === targetDate.getMonth()) {
          return date.addDays(date.endOfDay(targetDate), -1)
        }
      }
    }

    const nextMonthFirstDay = startOfMonth(addMonths(today, 1))
    const nextMonthTargetWeekDay = addWeeks(
      nextMonthFirstDay,
      scheduleWeekOfMonth - 1,
    )
    const nextMonthTargetDayOfWeek =
      nextMonthTargetWeekDay.getDay() !== 0
        ? nextMonthTargetWeekDay.getDay()
        : DayOfWeekEnum.Sunday
    if (nextMonthTargetDayOfWeek <= scheduleDayOfWeek) {
      const diffDays = scheduleDayOfWeek - nextMonthTargetDayOfWeek
      return date.addDays(nextMonthTargetWeekDay, diffDays)
    } else {
      const diffDays = 7 - (nextMonthTargetDayOfWeek - scheduleDayOfWeek)
      return date.addDays(nextMonthTargetWeekDay, diffDays)
    }
  }

  // 頻度=一度だけ
  if (schedule.frequency === ScheduleFrequencyEnum.Once) {
    return yesterday
  }

  // 頻度=カスタム
  if (schedule.frequency === ScheduleFrequencyEnum.Custom) {
    return yesterday
  }
  // いずれにもマッチしない場合
  return yesterday
}

const disabledPastToday = (current: Date | null, schedule: ScheduleForm) => {
  if (current === null) return false
  return current < getStartDateDisableBoundary(schedule)
}

/**
 * 開始日を有効な範囲内に丸める
 * @param value - チェック対象の日付
 * @param schedule - スケジュールフォーム
 * @returns 範囲内に補正された日付
 */
export const clampStartDate = (
  value: Date | null | undefined,
  schedule: ScheduleForm,
): Date | null | undefined => {
  if (value === null || value === undefined) return value

  // 境界値（時刻付き）をフォームにセットする補正先（00:00:00）に変換する
  // - boundary が日の途中（例: 昨日 23:59:59）の場合、翌日の 00:00:00 が補正先
  // - boundary がちょうど 00:00:00（月単位の特定日）の場合、その日自体が補正先
  const boundary = getStartDateDisableBoundary(schedule)
  const dayStart = date.startOfDay(boundary)
  const clampTarget =
    dayStart.getTime() < boundary.getTime()
      ? date.startOfDay(date.addDays(boundary, 1))
      : dayStart

  // 選択可能な最小日付より前の場合は補正する
  if (value < boundary) {
    return clampTarget
  }

  // 終了日が設定されている場合、終了日より後にならないようにする
  // ただし終了日自身が無効値（clampTarget 未満 = 既に過去）の場合は信用せず clampTarget にフォールバック
  // 例: 開始日・終了日どちらも過去のスケジュール編集時、value が一旦今日相当に変化したあと
  //     終了日（過去）にクランプされて過去の値で保存できてしまう事象を防ぐ
  if (schedule.endReportDate && value > schedule.endReportDate) {
    return schedule.endReportDate >= clampTarget
      ? schedule.endReportDate
      : clampTarget
  }

  return value
}

/**
 * 終了日を有効な範囲内に丸める
 * @param value - チェック対象の日付
 * @param schedule - スケジュールフォーム
 * @returns 範囲内に補正された日付
 */
export const clampEndDate = (
  value: Date | null | undefined,
  schedule: ScheduleForm,
): Date | null | undefined => {
  if (value === null || value === undefined) return value

  // shouldDisableEndReportDatePicker は current <= today で判定するため、
  // カレンダー UI と整合する最小の選択可能日は「翌日の midnight」
  // date.today() は現在時刻（HH:MM:SS）を返すため、midnight 基準で比較する
  const minSelectableDate = date.startOfDay(date.addDays(date.today(), 1))

  // 最小選択可能日より前の場合は補正する
  if (value < minSelectableDate) {
    return minSelectableDate
  }

  // 開始日が設定されている場合、開始日より前にならないようにする
  // ただし開始日自身が無効値（minSelectableDate 未満）の場合は信用せず minSelectableDate にフォールバック
  if (schedule.startReportDate && value < schedule.startReportDate) {
    return schedule.startReportDate >= minSelectableDate
      ? schedule.startReportDate
      : minSelectableDate
  }

  return value
}

const SchedulePeriodRow: React.FC<TBaseFormRowProps> = ({
  setFieldValue,
  values,
  errors,
  submitCount,
}) => {
  const { frequency, visibleEndReportDate, startReportDate, endReportDate } =
    values

  const onEndReportDateVisibilityChange = () =>
    setFieldValue('visibleEndReportDate', !visibleEndReportDate)
  const onStartReportDateChange = (date: Date | null) => {
    // ミリ秒以下を 0 にするのは、MySQL DATETIME(0) が .500 以上のミリ秒を繰り上げるため
    // 例: JST 23:59:59.999 → UTC 14:59:59.999 → MySQL 繰り上げ → UTC 15:00:00 (= JST 翌日 00:00:00)
    // ref: https://dev.mysql.com/doc/refman/8.0/en/fractional-seconds.html
    setFieldValue('startReportDate', date ? setMilliseconds(date, 0) : date)
  }
  const onEndReportDateChange = (date: Date | null) => {
    // NOTE: 終了日中はスケジュールが有効になるよう、選択日の終わりの日時を設定する
    // ミリ秒以下を 0 にするのは、MySQL DATETIME(0) が .500 以上のミリ秒を繰り上げるため
    // 例: JST 23:59:59.999 → UTC 14:59:59.999 → MySQL 繰り上げ → UTC 15:00:00 (= JST 翌日 00:00:00)
    // ref: https://dev.mysql.com/doc/refman/8.0/en/fractional-seconds.html
    setFieldValue(
      'endReportDate',
      date ? setMilliseconds(endOfDay(date), 0) : undefined,
    )
  }

  // MUI のDatePickerのshouldDisableDateは、
  // ユーザーが選択できない日付を非活性にするだけで、
  // 直接入力した日付のバリデーションは行わないため、
  // 入力後に有効な範囲内に丸める処理を追加している
  // 開始日:
  const onStartReportDateBlur = () => {
    const clampedDate = clampStartDate(startReportDate, values)
    if (clampedDate && clampedDate !== startReportDate) {
      // ミリ秒以下を 0 にするのは、MySQL DATETIME(0) が .500 以上のミリ秒を繰り上げるため
      // 例: JST 23:59:59.999 → UTC 14:59:59.999 → MySQL 繰り上げ → UTC 15:00:00 (= JST 翌日 00:00:00)
      // ref: https://dev.mysql.com/doc/refman/8.0/en/fractional-seconds.html
      setFieldValue('startReportDate', setMilliseconds(clampedDate, 0))
    }
  }

  // 終了日:
  const onEndReportDateBlur = () => {
    if (!endReportDate) return
    const clampedDate = clampEndDate(endReportDate, values)
    if (clampedDate && clampedDate !== endReportDate) {
      // NOTE: 終了日中はスケジュールが有効になるよう、選択日の終わりの日時を設定する
      // ミリ秒以下を 0 にするのは、MySQL DATETIME(0) が .500 以上のミリ秒を繰り上げるため
      // 例: JST 23:59:59.999 → UTC 14:59:59.999 → MySQL 繰り上げ → UTC 15:00:00 (= JST 翌日 00:00:00)
      // ref: https://dev.mysql.com/doc/refman/8.0/en/fractional-seconds.html
      setFieldValue('endReportDate', setMilliseconds(endOfDay(clampedDate), 0))
    }
  }

  const shouldDisableEndReportDatePicker = (current: Date | null) =>
    current ? current <= date.addDays(date.today(), 0) : false

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Stack spacing={1}>
        <Typography variant="subtitle1" fontWeight="bold">
          日付
        </Typography>
        {frequency !== ScheduleFrequencyEnum.Once && (
          <FormControlLabel
            control={
              <Checkbox
                checked={visibleEndReportDate}
                onChange={onEndReportDateVisibilityChange}
                sx={{ pointerEvents: 'auto' }}
              />
            }
            label="終了日を設定する"
            sx={{ pointerEvents: 'none' }}
          />
        )}
        <Stack direction="row" spacing={1} alignItems="center">
          <DatePicker
            shouldDisableDate={current => disabledPastToday(current, values)}
            orientation="portrait"
            format="yyyy/MM/dd"
            value={startReportDate ?? null}
            onChange={onStartReportDateChange}
            slotProps={{
              textField: {
                onBlur: onStartReportDateBlur,
                slotProps: {
                  htmlInput: {
                    placeholder: '開始日',
                  },
                },
              },
              toolbar: {
                hidden: true,
              },
            }}
            sx={{ flex: 1 }}
          />
          <Typography>
            {visibleEndReportDate ? <>〜</> : <>から開始</>}
          </Typography>
          {visibleEndReportDate && (
            <DatePicker
              shouldDisableDate={shouldDisableEndReportDatePicker}
              orientation="portrait"
              format="yyyy/MM/dd"
              value={endReportDate ?? null}
              onChange={onEndReportDateChange}
              slotProps={{
                textField: {
                  onBlur: onEndReportDateBlur,
                  slotProps: {
                    htmlInput: {
                      placeholder: '終了日',
                    },
                  },
                },
                toolbar: {
                  hidden: true,
                },
              }}
              sx={{ flex: 1 }}
            />
          )}
        </Stack>
        {submitCount > 0 && (
          <FormikErrorMessage name="startReportDate" errors={errors} />
        )}
        {submitCount > 0 && visibleEndReportDate && (
          <FormikErrorMessage name="endReportDate" errors={errors} />
        )}
      </Stack>
    </LocalizationProvider>
  )
}

export default SchedulePeriodRow
